import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { automationService } from '@/lib/automationService';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabaseClient';
import { Zap, Settings, History, Layout } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { getCurrentUserOrganizationId } from '@/lib/data.js';
import AutomationTemplates from './AutomationTemplates';

export default function AutomationSettings() {
  const { toast } = useToast();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const orgId = await getCurrentUserOrganizationId();
        setOrganizationId(orgId);
        
        if (orgId) {
          await loadExecutions(orgId);
        }
      } catch (error) {
        console.error('Error initializing automation settings:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, []);

  const loadExecutions = async (orgId) => {
    try {
      const data = await automationService.getAutomationExecutions(orgId, 20);
      setExecutions(data);
    } catch (error) {
      console.error('Error loading executions:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Completed' },
      failed: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Failed' },
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pending' },
      running: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'Running' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Automation System
            </CardTitle>
            <CardDescription>Loading automation system...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Automation System
            </CardTitle>
            <CardDescription>Activate and manage automation templates</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 text-center py-4">
              Unable to load automation system. Please ensure you are associated with an organization.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Automation System
          </CardTitle>
          <CardDescription>
            Activate pre-built automation templates and monitor their execution
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue="templates" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Layout className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="executions" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Execution History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <AutomationTemplates />
        </TabsContent>

        <TabsContent value="executions">
          <Card>
            <CardHeader>
              <CardTitle>Execution History</CardTitle>
              <CardDescription>Track recent automation executions and their results</CardDescription>
            </CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <div className="text-center py-8">
                  <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No automation executions yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Activate some templates to start seeing automation activity
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {executions.map((execution) => (
                    <div key={execution.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-sm">
                            {execution.automation_rules?.name || 'Unknown Rule'}
                          </h4>
                          {getStatusBadge(execution.status)}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {new Date(execution.executed_at).toLocaleString()}
                        </p>
                        {execution.error_message && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Error: {execution.error_message}
                          </p>
                        )}
                        {execution.result_data && (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Result: {JSON.stringify(execution.result_data)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 